import {Callback, Mix} from "../../Core/Reflection";
import {Draggable} from "../Draggable";
import {BaseButton, HoverableButton, TextureButton} from "../../Core/Button";
import {Signal} from "../../Core/Signal";
import * as PIXI from "pixi.js";
import {PointUtil} from "../../Core/Geom";
import {Ease} from "../../Core/Animatable";
import {PulsatingButton} from "../Button";
import {TransformBackup} from "../../Core/TransformBackup";
import {Focusable} from "../Focusable";
import {Container} from "../../Core/Container";
import {VoiceOverManager} from "../../Managers/VoiceOverManager";
import {voOptionsButtons} from "../../Data/VoiceOver";

export class CookingItem extends Mix(TextureButton, TransformBackup, PulsatingButton, HoverableButton([1.05, 1]), Draggable, Focusable) {

    public readonly onStartDrag = Signal<(e: PIXI.InteractionEvent) => void>();
    public readonly onStopDrag = Signal<(e: PIXI.InteractionEvent) => void>();
    public readonly onDrag = Signal<() => void>();

    private _target?: BaseButton;
    public targetTransform?: { x?: number, y?: number, scale?: number, rotation?: number };
    public targetDistance?: number;
    public draggable = true;
    public onSnap = Signal<() => void>();
    public onCancel = Signal<() => void>();
    public onDragged = Signal<() => void>();
    public onKeyboard = Signal<() => void>();

    private clicked = false;
    private dropClickTimeout?: number;
    private ignoreClickOutside?: boolean;
    private isSquishStretch?: boolean;
    private squishStretchScaleX?: number;
    private squishStretchScaleY?: number;

    hoverVO?: string;

    constructor() {
        super();

        let x = 0;
        let y = 0;
        let sx = 1;
        let sy = 1;
        let r = 0;
        const p = {x: 0, y: 0};
        let distance = 0;

        this.onStartDrag.add(() => {
            x = this.x;
            y = this.y;
            sx = this.scale.x;
            sy = this.scale.y;
            r = this.rotation;
            if (this._target) {
                p.x = this.targetTransform?.x ?? this._target.x;
                p.y = this.targetTransform?.y ?? this._target.y;
                distance = PointUtil.distance(this, p);
            }
            if (this.draggable) {
                this.zIndex = -1;
            }
        });
        this.onStopDrag.add(() => {
            if (this.draggable) {
                if (this._target && PointUtil.distance(this, p) < (this.targetDistance ?? Math.max(this._target.width, this._target.height) * 0.5)) {
                    this.enabled = false;
                    this.snapToTarget(this.onDragged);
                } else {
                    this.position.set(x, y);
                    this.scale.set(sx, sy);
                    this.rotation = r;
                    this.onCancel();
                }
            } else {
                this.onDragged();
            }
        });
        this.onDrag.add(() => {
            if (this.draggable && this._target && this.targetTransform) {
                const d = PointUtil.distance(this, p);
                if (d < distance) {
                    if (this.targetTransform.scale !== undefined) {
                        this.scale.set(
                            this.targetTransform.scale + (sx - this.targetTransform.scale) * d / distance,
                            this.targetTransform.scale + (sy - this.targetTransform.scale) * d / distance);
                    }
                    if (this.targetTransform.rotation !== undefined) {
                        this.rotation = this.targetTransform.rotation + (r - this.targetTransform.rotation) * d / distance;
                    }
                } else {
                    this.scale.set(sx, sy);
                    this.rotation = r;
                }
            }
        });

        this.onClick.add(() => this.onClickSequence(this));
        this.onOver.add(() => {
            if (this.hoverVO && !PIXI.utils.isMobile.any) {
                VoiceOverManager.instance.playVO(this.hoverVO, {
                    ...voOptionsButtons,
                    callback: b => {
                        // Play hover VO only once
                        if (b) this.hoverVO = undefined
                    }
                });
            }
        });
        this.onDown.add(() => {
            if (this.hoverVO && PIXI.utils.isMobile.any) {
                VoiceOverManager.instance.playVO(this.hoverVO, voOptionsButtons);
            }
        });
    }

    get target(): BaseButton | undefined {
        return this._target;
    }

    set target(value: BaseButton | undefined) {
        if (this._target) {
            this._target.onClick.remove(this);
        }
        this._target = value;
        if (this._target) {
            this._target.onClick.add(() => {
                this.onClickSequence(this._target!);
            }, this);
        }
    }

    private onClickSequence(target: BaseButton) {
        this.ignoreClickOutside = true;
        const onClickOutside = () => {
            if (!this.ignoreClickOutside) {
                this.dropClickTimeout = window.setTimeout(() => {
                    this.clicked = false;
                }, 100);
            }
            document.removeEventListener("pointerup", onClickOutside, true);
            window.removeEventListener("keydown", onClickOutside, true);
        };
        window.clearTimeout(this.dropClickTimeout);
        document.removeEventListener("pointerup", onClickOutside, true);
        window.removeEventListener("keydown", onClickOutside, true);
        if (target === this) {
            this.clicked = true;
            this.dropClickTimeout = window.setTimeout(() => {
                this.ignoreClickOutside = false;
                document.addEventListener("pointerup", onClickOutside, true);
                window.addEventListener("keydown", onClickOutside, true);
            }, 100);
        } else if (target === this._target && this.clicked) {
            this.clicked = false;
            this.enabled = false;
            this.zIndex = -1;
            this.snapToTarget(this.onDragged);
        } else {
            this.clicked = false;
        }
    }

    snapToTarget(cb?: Callback) {
        if (!this._target) {
            if (cb) cb();
        } else {
            const s = 1200;
            const p = {
                x: this.targetTransform?.x ?? this._target.x,
                y: this.targetTransform?.y ?? this._target.y,
            };
            this.tweenTo<CookingItem>(PointUtil.distance(this, p) / s, Ease.sineInOut, {
                x: p.x,
                y: p.y,
                scaleX: this.targetTransform?.scale ?? this.scale.x,
                scaleY: this.targetTransform?.scale ?? this.scale.y,
                rotation: this.targetTransform?.rotation ?? this.rotation,
                onUpdate: this.onSnap,
                onComplete: cb
            });
        }
    }

    public squishStretch(cb?: Callback) {
        const sx = this.isSquishStretch ? this.squishStretchScaleX ?? this.scale.x : this.scale.x;
        const sy = this.isSquishStretch ? this.squishStretchScaleY ?? this.scale.y : this.scale.y;
        if (!this.isSquishStretch) {
            this.squishStretchScaleX = this.scale.x;
            this.squishStretchScaleY = this.scale.y;
        }
        this.isSquishStretch = true;
        this.squishStretchStep([
            {target: this, x: 1.20 * sx, y: 0.80 * sy, t: 0.15},
            {target: this, x: 0.90 * sx, y: 1.40 * sy, t: 0.20},
            {target: this, x: 1.10 * sx, y: 0.95 * sy, t: 0.15},
            {
                target: this, x: sx, y: sy, t: 0.10, cb: () => {
                    this.isSquishStretch = false;
                    if (cb) cb();
                }
            }
        ]);
    }

    private squishStretchStep(points: { target: Container, x: number, y: number, t: number, cb?: Callback }[]) {
        const point = points.shift()!;
        point.target.tweenTo(point.t, Ease.sineInOut, {
            scaleX: point.x,
            scaleY: point.y,
            onComplete: () => {
                if (point.cb) {
                    point.cb();
                }
                if (points.length > 0) {
                    this.squishStretchStep(points);
                }
            }
        });
    }

    onFocusActivated(): void {
        if (this.enabled && this.worldVisible) {
            if (this.onClick) {
                this.onDown(this);
                this.onClick(this);
                if (this.onDragged.has()) {
                    this.enabled = false;
                    this.zIndex = -1;
                    this.onKeyboard();
                    if (this.draggable) {
                        this.snapToTarget(this.onDragged);
                    } else {
                        this.onDragged();
                    }
                }
            }
        }
    }

    destroy() {
        this.onSnap.remove();
        this.onCancel.remove();
        this.onDragged.remove();
        super.destroy();
    }
}